import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonNav, ModalController } from '@ionic/angular';
import { faArrowLeft, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { FullPageLayoutComponent } from '@app/core/layout/full-page-layout/full-page-layout.component';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { CodeVerificationComponent } from '@app/shared/components/code-verification/code-verification.component';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { SignDocumentBatchSandbox } from '@app/document-bundle-signing/sandboxes/sign-document-batch.sandbox';

@Component({
  selector: 'c-sign-document-batch-verification',
  imports: [
    FullPageLayoutComponent,
    ButtonComponent,
    SharedModule,
    ReactiveFormsModule,
    TranslocoPipe,
    CodeVerificationComponent,
  ],
  templateUrl: './verification-page.component.html',
  styleUrl: './verification-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignDocumentBatchVerificationPageComponent implements OnInit {
  private readonly sandbox = inject(SignDocumentBatchSandbox);
  private readonly nav = inject(IonNav);
  private readonly modalCtrl = inject(ModalController);
  private readonly toastSandbox = inject(ToastSandbox);
  private readonly destroyRef = inject(DestroyRef);

  readonly backIcon = faArrowLeft;
  readonly closeIcon = faXmark;

  readonly verificationCodeForm = new FormGroup({
    verificationCode: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  // Match the design's 5 large code boxes; the shared c-otp-input default is smaller.
  readonly otpInputStyles: Record<string, string> = {
    flex: '1',
    height: '64px',
    'font-size': '18px',
    'text-align': 'center',
    'border-radius': 'var(--radius-xl)',
    border: '1px solid var(--border-primary, #dcdfe4)',
    'background-color': 'var(--gray-050, #f9fafb)',
  };

  readonly otpLength = computed(() => this.sandbox.verificationCodeLength() ?? 5);
  readonly maskedPhone = this.sandbox.maskedPhoneNumber;
  readonly loading = signal(false);

  private readonly code = signal('');
  readonly disableVerify = computed(() => this.code().length !== this.otpLength());

  ngOnInit(): void {
    // The SMS code is triggered by the overview's "Sign" action before this
    // page is pushed (mirrors the single-document sign flow), so we only wire
    // up the code length here. Resending is handled by resend().
    this.verificationCodeForm.controls.verificationCode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.code.set(value ?? ''));
  }

  verify(): void {
    if (this.disableVerify() || this.loading()) {
      return;
    }

    this.loading.set(true);
    this.sandbox
      .verify(this.verificationCodeForm.controls.verificationCode.value)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.toastSandbox.showSuccessToast({ key: 'my-documents.sign-batch.verify.success' });
          this.modalCtrl.dismiss({ signed: true });
        },
        error: () => this.toastSandbox.showErrorToast({ key: 'error.invalid-verification-code' }),
      });
  }

  resend(): void {
    this.sandbox.startVerification();
  }

  back(): void {
    this.nav.pop();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }
}
